/**
 * This is the container component for the AssessmentStatusWidget written in React functional component style in TypeScript.
 * This container passes the necessary props to the AssessmentStatusWidget.
 * It also handles any actions that need to be dispatched to the Redux store.
 * It handles data fetching (using dummy data in this case).
 * It instantiates the AssessmentStatusWidgetModel and passes it to the AssessmentStatusWidget.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { WidgetAssessmentStatus } from './AssessmentStatus';
import { AsyncLoadingState, WidgetCategory, WidgetType } from '../enums';
import { AssessmentStatusWidgetModel } from '../models';
import { WidgetWrapper } from './WidgetWrapper';
import {
  selectAssessmentStatusWidgetData,
  fetchAssessmentStatusWidgetData,
} from '../store/slices/assessmentStatusWidgetSlice';
import useSelectedClassroom from '@/hooks/useSelectedClassroom';
import {
  dashboardWidgetState,
  toggleWidget,
  WidgetStateEnum,
} from '@/store/slices';
import { useTranslation } from 'react-i18next';
import assessmentOutcomesIcon from "@/images/icons/brain.png";
import { findAssessmentWindow } from '../utils/findAssessmentWindow';
import { useAssessmentPeriodWindows } from '@/hooks/useAssessmentPeriods';
import { useRosterInfo } from '@/hooks/useRosterInfo';
import { useCalendar } from '@/hooks/useCalendar';
import { Student } from '@/models';
import { RootState } from '@/models';

// Define the AssessmentStatusWidgetContainer functional component
export const AssessmentStatusWidgetContainer: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('dashboard');


  // Fetch the AssessmentStatusWidget data from the Redux store
  const { loading, data, error } = useSelector(
    selectAssessmentStatusWidgetData
  );
  const { districtId, schoolId } = useRosterInfo();
  const students = useSelector((state: RootState) => state.reports?.roster?.data?.student);
  const [status, setStatus] = useState('none');
  const { assessment } = useSelector(dashboardWidgetState);
  const assessmentCalendar = useAssessmentPeriodWindows();

  const entityIdList = useMemo(() => {
    if (!students) return [];
    return students.map((student: Student) => student.id);
  }, [students]);

  useEffect(() => {

    if (loading === AsyncLoadingState.LOADING || assessmentCalendar.loading === true) {
      setStatus('loading');
    } else if (loading === AsyncLoadingState.ERROR || error) {
      setStatus('error');
    } else if (loading === AsyncLoadingState.SUCCESS) {
      setStatus('success');
    } else if (loading === AsyncLoadingState.SUCCESS && data) {
      setStatus('none');
    } else {
      setStatus('none');
    }
  }, [loading, error, assessmentCalendar.loading, data]);

  const assessmentWindowStatusData = useMemo(() => {
    return findAssessmentWindow(assessmentCalendar.assessmentPeriodWindows);
  }, [assessmentCalendar.assessmentPeriodWindows]);
  
  // Get the current window type (District vs School assignment)
  const currentWindowType = useMemo(() => {
    if (assessmentWindowStatusData?.activeDistrictWindow) {
      // Find the current active window in assessment period windows to get entity type
      const currentWindow = assessmentCalendar.assessmentPeriodWindows?.find(window => 
        window.current === true && 
        (window.screeningWindowType === 'DistrictAssignment' || 
         window.screeningWindowType === 'SchoolAssignment')
      );
      
      return currentWindow?.screeningWindowType || 'DistrictAssignment';
    }
    return 'DistrictAssignment'; // Default fallback
  }, [assessmentCalendar.assessmentPeriodWindows, assessmentWindowStatusData]);

  useEffect(() => {

    if (entityIdList.length > 0 && schoolId && districtId && assessmentWindowStatusData.currentPeriodIdentifier != null) {

      dispatch(fetchAssessmentStatusWidgetData({
        entityIdList: entityIdList,
        schoolId: schoolId,
        districtId: districtId,
        currentPeriodId: assessmentWindowStatusData.currentPeriodIdentifier,
        entityRoster: students,
        assessmentWindowStatus: assessmentWindowStatusData,
        currentWindowType: currentWindowType
      }) as any);
    } else {

    }
  }, [schoolId, districtId, assessmentWindowStatusData.currentPeriodIdentifier, students, currentWindowType]);

  // Function to handle retry when data fails to load
  const handleRetry = () => {
    if (entityIdList.length > 0 && schoolId && districtId && assessmentWindowStatusData.currentPeriodIdentifier) {
      dispatch(fetchAssessmentStatusWidgetData({
        entityIdList: entityIdList,
        schoolId: schoolId,
        districtId: districtId,
        currentPeriodId: assessmentWindowStatusData.currentPeriodIdentifier,
        entityRoster: students,
        assessmentWindowStatus: assessmentWindowStatusData,
        currentWindowType: currentWindowType
      }) as any);
    }
  };


  // Instantiate the AssessmentStatusWidgetModel where it can apply business logic to the data as needed
  const assessmentStatusWidgetModel = useMemo(() => {
    return new AssessmentStatusWidgetModel(data);
  }, [data]);

  const loadingState = entityIdList.length > 0 && assessmentCalendar.assessmentPeriodWindows.length > 0 && assessmentWindowStatusData ? loading : AsyncLoadingState.LOADING;

  return (
    <WidgetWrapper
      title="Assessment Status"
      loadingState={loadingState}
      type={WidgetType.ASSESSMENT_STATUS}
      category={WidgetCategory.ASSESS}
      icon={assessmentOutcomesIcon}
      isCollapsed={assessment.status === WidgetStateEnum.COLLAPSED}
      onCollapserHandler={() =>
        dispatch(toggleWidget({ group: 'assessment', widget: 'status' }))
      }
      id="assessment-status"
      dataTestId="assessment-status-widget">
      <WidgetAssessmentStatus
        assessmentWindowStatus={assessmentWindowStatusData}
        data={assessmentStatusWidgetModel}
        widgetLoadingState={loadingState}
        status={status}
        onRetry={handleRetry}
      />
    </WidgetWrapper>
  );
};

export default AssessmentStatusWidgetContainer;
